import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Boxes, Check } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/mdh/Nav";
import { API } from "@/lib/api";


export default function Templates() {
    const [templates, setTemplates] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${API}/templates`, { credentials: "include" });
                if (r.status === 401) {
                    navigate("/login");
                    return;
                }
                const d = await r.json();
                setTemplates(d.templates || []);
            } catch {
                setTemplates([]);
            }
        })();
    }, [navigate]);

    const deploy = async (t) => {
        setBusyId(t.id);
        try {
            const r = await fetch(`${API}/apps`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: t.name,
                    description: t.tagline,
                    spec: "",
                    template: t.id,
                }),
            });
            if (!r.ok) throw new Error("failed");
            const d = await r.json();
            sessionStorage.setItem(`mdh-new-key-${d.app.app_id}`, d.key);
            toast.success("App created", {
                description: "Your key is waiting on the setup card.",
            });
            navigate(`/dashboard?app=${d.app.app_id}`);
        } catch {
            toast.error("Could not deploy template");
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div data-testid="templates-page" className="relative min-h-screen">
            <div className="bg-grid-slate pointer-events-none absolute inset-0 opacity-40" aria-hidden="true" />
            <header className="relative mx-auto flex h-16 max-w-6xl items-center justify-between px-5 lg:px-8">
                <Logo />
                <Link
                    to="/dashboard"
                    data-testid="templates-back-link"
                    className="flex items-center gap-1.5 font-mono text-[11px] text-slate-500 hover:text-cyan-300"
                >
                    <ArrowLeft className="h-3 w-3" /> Dashboard
                </Link>
            </header>

            <div className="relative mx-auto max-w-6xl px-5 pb-24 pt-12 lg:px-8">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-teal-400/80">
                    <Boxes className="mr-2 inline h-3.5 w-3.5" />
                    Templates
                </p>
                <h1 className="font-hero mt-4 text-3xl font-bold tracking-tight text-slate-100 sm:text-4xl">
                    Pre-built apps, ready to run.
                </h1>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-400">
                    Each template is a configured monitoring or integration app. Deploy in one
                    click, then tune the configuration to your setup.
                </p>

                <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {templates === null ? (
                        <p className="font-mono text-xs text-slate-500">Loading templates…</p>
                    ) : (
                        templates.map((t) => (
                            <div
                                key={t.id}
                                data-testid={`template-card-${t.id}`}
                                className="group flex flex-col rounded-xl border border-slate-800/80 bg-[#111827] p-6 transition-all duration-300 hover:border-cyan-500/30"
                            >
                                <h3 className="font-display text-lg font-bold tracking-tight text-slate-100">
                                    {t.name}
                                </h3>
                                <p className="mt-1.5 text-xs font-medium text-cyan-300/90">
                                    {t.tagline}
                                </p>
                                <p className="mt-3 flex-1 text-xs leading-relaxed text-slate-400">
                                    {t.description}
                                </p>
                                <ul className="mt-4 space-y-1.5">
                                    {t.features.map((f) => (
                                        <li key={f} className="flex items-start gap-2 text-[11px] text-slate-400">
                                            <Check className="mt-0.5 h-3 w-3 shrink-0 text-teal-400" />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-4 flex flex-wrap gap-1.5">
                                    {t.config.map((c) => (
                                        <span
                                            key={c}
                                            className="rounded border border-slate-700/80 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-slate-500"
                                        >
                                            {c}
                                        </span>
                                    ))}
                                </div>
                                <button
                                    data-testid={`template-deploy-${t.id}`}
                                    onClick={() => deploy(t)}
                                    disabled={busyId === t.id}
                                    className="group/btn mt-6 flex items-center justify-center gap-2 rounded-md bg-cyan-400 px-4 py-2.5 text-xs font-bold text-slate-950 transition-colors hover:bg-cyan-300 disabled:opacity-50"
                                >
                                    {busyId === t.id ? "Deploying…" : "Deploy"}
                                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
